import { Link } from "react-router-dom";
import { ArrowRight, Heart } from "lucide-react";
import { IMAGES, LOGO, SERVICES } from "../lib/content";
import { SectionTitle, Sparkle, StitchDivider, ThreadLine } from "../components/site/Decor";
import { OrderCTA } from "../components/site/OrderCTA";
import { GalleryPreview } from "../components/site/GalleryPreview";
import { Promotions } from "../components/site/Promotions";
import { Reviews } from "../components/site/Reviews";

const Hero = () => (
  <section className="grain relative overflow-hidden bg-[#FFF4F8]" data-testid="home-hero">
    <ThreadLine className="pointer-events-none absolute inset-x-0 top-24 h-48 w-full opacity-80" />
    <Sparkle className="absolute left-[8%] top-16" size={22} />
    <Sparkle className="absolute right-[12%] top-24" size={16} delay={0.8} />
    <Sparkle className="absolute bottom-20 left-[45%]" size={12} delay={1.6} />
    <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-6 pb-20 pt-12 lg:grid-cols-[1.15fr_1fr] lg:pt-16">
      <div>
        <img src={LOGO} alt="KMK Customs Embroidery" className="rise w-full max-w-[560px]" data-testid="hero-logo" />
        <h1 className="rise mt-8 max-w-xl font-display text-3xl font-bold leading-tight sm:text-4xl lg:text-5xl" style={{ animationDelay: ".1s" }}>
          Where creativity meets <span className="italic text-[#FF1F8E]">custom style.</span>
        </h1>
        <p className="rise mt-5 max-w-lg text-base text-[#0E0B0D]/70 sm:text-lg" style={{ animationDelay: ".18s" }}>
          Personalized embroidery and apparel for families, schools, teams and businesses, made one stitch at a time.
        </p>
        <div className="rise mt-8 flex flex-wrap gap-3" style={{ animationDelay: ".26s" }}>
          <Link to="/order" className="btn-pink" data-testid="hero-start-order-btn">Start a Custom Order <ArrowRight className="h-4 w-4" /></Link>
          <Link to="/gallery" className="btn-outline-gold" data-testid="hero-view-gallery-btn">See Our Work</Link>
        </div>
      </div>
      <div className="relative mx-auto w-full max-w-md">
        <div className="gold-trim absolute -right-4 -top-4 h-full w-full rounded-[2.5rem]" />
        <img src={IMAGES.hero} alt="Pisces custom embroidered sweatshirt by KMK Customs" className="relative aspect-[4/5] w-full rounded-[2.5rem] object-cover shadow-2xl" />
        <div className="absolute -bottom-6 -left-6 rounded-2xl bg-[#0E0B0D] px-5 py-4 text-white shadow-xl">
          <p className="font-script text-2xl text-[#EBD08A]">Handmade</p>
          <p className="text-xs uppercase tracking-[0.2em]">with love</p>
        </div>
      </div>
    </div>
  </section>
);

const Featured = () => (
  <section className="mx-auto max-w-7xl px-6 py-20" data-testid="home-featured">
    <SectionTitle eyebrow="What we make" title="Featured" script="Favorites" />
    <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-12">
      {SERVICES.map((s, i) => (
        <Link key={s.slug} to={`/services#${s.slug}`} data-testid={`featured-${s.slug}`}
          className={`group relative overflow-hidden rounded-3xl bg-[#0E0B0D] ${i < 2 ? "lg:col-span-6 h-80" : "lg:col-span-4 h-72"}`}>
          <img src={s.image} alt={s.title} className="absolute inset-0 h-full w-full object-cover opacity-75 transition-transform duration-700 group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
          <div className="absolute bottom-0 p-6 text-white">
            <p className="font-script text-3xl text-[#FF6FB5]">{s.script}</p>
            <h3 className="font-display text-xl font-bold sm:text-2xl">{s.title}</h3>
            <span className="mt-2 inline-flex items-center gap-1 text-sm text-[#EBD08A] opacity-0 transition-opacity duration-300 group-hover:opacity-100">Explore <ArrowRight className="h-3.5 w-3.5" /></span>
          </div>
        </Link>
      ))}
    </div>
  </section>
);

const AboutBlurb = () => (
  <section className="relative overflow-hidden bg-[#0E0B0D] text-white" data-testid="home-about">
    <Sparkle className="absolute right-16 top-16" size={20} />
    <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 py-20 lg:grid-cols-2">
      <img src={IMAGES.about} alt="Embroidery in progress" className="aspect-[4/3] w-full rounded-3xl object-cover" />
      <div>
        <SectionTitle eyebrow="Our story" title="Small business," script="big heart" light />
        <p className="mt-6 text-white/75">KMK Customs Embroidery started with a love of thread and a passion for making people feel special. Every hat, stole, tee and sweatshirt is made by hand with care, because you deserve pieces as unique as you are.</p>
        <p className="mt-4 flex items-center gap-2 text-[#EBD08A]"><Heart className="h-4 w-4 fill-[#FF1F8E] text-[#FF1F8E]" /> Thank you for supporting a small business.</p>
        <Link to="/about" className="btn-outline-gold mt-8 text-white" data-testid="home-about-link">Read our story</Link>
      </div>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Hero />
      <Featured />
      <Promotions />
      <AboutBlurb />
      <Reviews />
      <section className="mx-auto max-w-7xl px-6 pt-20">
        <SectionTitle eyebrow="Fresh off the machine" title="Recent" script="Work" />
        <GalleryPreview />
        <StitchDivider className="mt-16" />
      </section>
      <OrderCTA />
    </>
  );
}
